import React, { useEffect, useRef } from "react";
import { formatDistanceToNow } from "date-fns";
import { Flag, MessageSquare, Phone, Voicemail } from "lucide-react";
import CommunicationItem from "@/model/interface/CommunicationItem";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import VoicemailPlayer from "@/components/messages/VoicemailPlayer";
import ThreadOptionsMenu from "@/components/messages/ThreadOptionsMenu";
import { cn } from "@/lib/utils";
import { getCallIcon } from "@/util/call";
import { getContactDisplayName, getInitials } from "@/util/string";

interface InboxFeedRowProps {
  item: CommunicationItem;
  selected: boolean;
  highlighted?: boolean;
  archived?: boolean;
  searchResult?: boolean;
  onOpen: (item: CommunicationItem) => void;
}

const InboxFeedRow: React.FC<InboxFeedRowProps> = ({
  item,
  selected,
  highlighted = false,
  archived = false,
  searchResult = false,
  onOpen,
}) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const contact = item.contact;
  const name = getContactDisplayName(contact) ?? item.phoneNumber ?? "Unknown";
  const initials = contact ? getInitials(contact.firstName, contact.lastName) : "?";
  const outbound = item.direction === "OUTBOUND";
  const preview = item.kind === "TEXT" && outbound ? `You: ${item.preview ?? ""}` : item.preview ?? "";
  const attentionCount = item.attentionCount ?? 0;
  const emphasised = searchResult ? item.needsAttention : attentionCount > 0;
  const showsMenu = !searchResult && (!!contact?.id || !!item.conversationId);

  const channelIcon = () => {
    if (item.kind === "TEXT") {
      return <MessageSquare className={`h-3.5 w-3.5 ${outbound ? "text-info" : "text-success"}`} />;
    }
    if (item.kind === "VOICEMAIL") {
      return <Voicemail className="h-3.5 w-3.5 text-info" />;
    }
    if (item.call) {
      return getCallIcon(item.call.direction, item.call.status);
    }
    return <Phone className="h-3.5 w-3.5 text-muted-foreground" />;
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onOpen(item);
    }
  };

  const stopPropagation = (event: React.SyntheticEvent) => {
    event.stopPropagation();
  };

  useEffect(() => {
    if (!highlighted) {
      return;
    }
    rowRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [highlighted]);

  return (
    <div
      ref={rowRef}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(item)}
      onKeyDown={handleKeyDown}
      className={cn(
        "group flex w-full cursor-pointer items-start gap-3 border-b border-border p-3 text-left transition-colors hover:bg-accent",
        selected && "bg-accent",
        highlighted && "bg-primary/10",
      )}
    >
      <Avatar className="h-9 w-9 shrink-0">
        {contact?.photo && <AvatarImage src={contact.photo} />}
        <AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">{initials}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className={cn("truncate text-sm", emphasised ? "font-semibold" : "font-medium")}>{name}</span>
          <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
            {formatDistanceToNow(new Date(item.occurredAt), { addSuffix: true })}
          </span>
        </div>
        <div className="mt-0.5 flex items-center gap-1.5">
          <span className="shrink-0">{channelIcon()}</span>
          <span className={cn("truncate text-sm", emphasised ? "text-foreground" : "text-muted-foreground")}>{preview}</span>
          {!searchResult && attentionCount > 0 && (
            <span className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-info px-1.5 text-[11px] font-semibold text-info-foreground tabular-nums">
              {attentionCount}
            </span>
          )}
        </div>
        <div className="mt-1 flex h-7 items-center gap-2">
          {item.dealStageName && (
            <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-border px-2 text-[11px] font-medium leading-5">
              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: item.dealStageColor }} />
              {item.dealStageName}
            </span>
          )}
          {item.flagged && !archived && <Flag className="h-3.5 w-3.5 text-warning" fill="currentColor" />}
          {showsMenu && (
            <span className="ml-auto" onClick={stopPropagation} onKeyDown={stopPropagation}>
              <ThreadOptionsMenu
                contactId={contact?.id}
                conversationId={item.conversationId}
                flagged={item.flagged}
                archived={archived}
                triggerClassName="opacity-0 group-hover:opacity-100 focus:opacity-100 data-[state=open]:opacity-100"
              />
            </span>
          )}
        </div>
        {item.kind === "VOICEMAIL" && item.call && !searchResult && (
          <div className="mt-2" onClick={stopPropagation} onKeyDown={stopPropagation}>
            <VoicemailPlayer call={item.call} />
          </div>
        )}
      </div>
    </div>
  );
};

export default InboxFeedRow;
